import { Info } from "lucide-react";

import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RoleplayRank } from "@/graphql";

interface CompletionRequirementsProps {
  scenarioRankMin?: string;
  scenarioAttemptMin?: number;
  scenarioAttemptMax?: number;
  onRankMinChange: (value?: string) => void;
  onAttemptMinChange: (value?: number) => void;
  onAttemptMaxChange: (value?: number) => void;
  showTitle?: boolean;
  className?: string;
}

export function CompletionRequirements({
  scenarioRankMin,
  scenarioAttemptMin,
  scenarioAttemptMax,
  onRankMinChange,
  onAttemptMinChange,
  onAttemptMaxChange,
  showTitle = true,
  className = "",
}: CompletionRequirementsProps) {
  const handleAttemptMinChange = (value: string) => {
    const numValue =
      value === "0" ? undefined : value ? parseInt(value, 10) : undefined;
    onAttemptMinChange(numValue);

    // If setting min > max, clear max
    if (numValue && scenarioAttemptMax && numValue > scenarioAttemptMax) {
      onAttemptMaxChange(undefined);
    }
  };

  const handleAttemptMaxChange = (value: string) => {
    const numValue =
      value === "0" ? undefined : value ? parseInt(value, 10) : undefined;
    onAttemptMaxChange(numValue);

    // If setting max < min, clear min
    if (numValue && scenarioAttemptMin && numValue < scenarioAttemptMin) {
      onAttemptMinChange(undefined);
    }
  };

  const handleRankMinChange = (value: string) => {
    const finalValue =
      value === RoleplayRank.Unranked ? undefined : value || undefined;
    onRankMinChange(finalValue);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {showTitle && (
        <>
          <Label className="text-base font-semibold mb-1">
            Completion Criteria
          </Label>
          <p className="text-sm text-gray-500">
            Define the conditions for successful completion
          </p>
        </>
      )}

      <div className="space-y-4 mt-4">
        {/* Minimum Score */}{" "}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base">Minimum score to pass</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent side="top">
                Sets minimum requirements of the participant's best attempt on
                each scenario in order to pass
              </TooltipContent>
            </Tooltip>
          </div>
          <Select
            value={scenarioRankMin || RoleplayRank.Unranked}
            onValueChange={handleRankMinChange}
          >
            <SelectTrigger className="w-40 h-[50px] shadow-none text-base">
              <SelectValue placeholder="Any" className="text-base">
                {scenarioRankMin ? (
                  <div className="flex items-center gap-2">
                    <RankIcon
                      rank={scenarioRankMin as RoleplayRank}
                      type="circle"
                      size={16}
                    />
                    <span>
                      {scenarioRankMin === RoleplayRank.Bronze
                        ? "Bronze"
                        : scenarioRankMin === RoleplayRank.Silver
                          ? "Silver"
                          : scenarioRankMin === RoleplayRank.Gold
                            ? "Gold"
                            : "Any"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <RankIcon
                      rank={RoleplayRank.Unranked}
                      type="circle"
                      size={16}
                    />
                    <span>Any</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value={RoleplayRank.Unranked} className="text-base">
                <div className="flex items-center gap-2">
                  <RankIcon
                    rank={RoleplayRank.Unranked}
                    type="circle"
                    size={16}
                  />
                  <span>Any</span>
                </div>
              </SelectItem>
              <SelectItem value={RoleplayRank.Bronze} className="text-base">
                <div className="flex items-center gap-2">
                  <RankIcon
                    rank={RoleplayRank.Bronze}
                    type="circle"
                    size={16}
                  />
                  <span>Bronze (60%)</span>
                </div>
              </SelectItem>
              <SelectItem value={RoleplayRank.Silver} className="text-base">
                <div className="flex items-center gap-2">
                  <RankIcon
                    rank={RoleplayRank.Silver}
                    type="circle"
                    size={16}
                  />
                  <span>Silver (80%)</span>
                </div>
              </SelectItem>
              <SelectItem value={RoleplayRank.Gold} className="text-base">
                <div className="flex items-center gap-2">
                  <RankIcon rank={RoleplayRank.Gold} type="circle" size={16} />
                  <span>Gold (90%)</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Minimum Attempts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base">Minimum number of attempts</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent side="top">
                Minimum number of attempts participants must make on each
                scenario
              </TooltipContent>
            </Tooltip>
          </div>
          <Select
            value={scenarioAttemptMin?.toString() || "0"}
            onValueChange={handleAttemptMinChange}
          >
            <SelectTrigger className="w-40 h-[50px] shadow-none text-base">
              <SelectValue className="text-base" placeholder="No Minimum" />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="0" className="text-base">
                No Minimum
              </SelectItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <SelectItem
                  className="text-base"
                  key={num}
                  value={num.toString()}
                  disabled={
                    scenarioAttemptMax ? num > scenarioAttemptMax : false
                  }
                >
                  {num}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Maximum Attempts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base">Maximum number of attempts</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent side="top">
                Limit the number of attempts participants can make on each
                scenario
              </TooltipContent>
            </Tooltip>
          </div>
          <Select
            value={scenarioAttemptMax?.toString() || "0"}
            onValueChange={handleAttemptMaxChange}
          >
            <SelectTrigger className="w-40 h-[50px] shadow-none text-base">
              <SelectValue className="text-base" placeholder="No Limit" />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="0" className="text-base">
                No Limit
              </SelectItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <SelectItem
                  className="text-base"
                  key={num}
                  value={num.toString()}
                  disabled={
                    scenarioAttemptMin ? num < scenarioAttemptMin : false
                  }
                >
                  {num}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
